<template>
  <app-layout>
    <Head title="Sent"/>
    <template #header>
      <PageBackHeader
        :href="route('messages.sent')"
        label="Sent Messages"
      >
        <template #subtitle>
          <p class="text-gray-600 font-semibold text-xl">
            Message ID {{ messageDetails.message_id }}
          </p>
        </template>
      </PageBackHeader>
    </template>

    <div class="max-w-7xl mx-auto mb-6">
      <div class="w-full p-2 sm:px-6 lg:px-8">
        <div class="grid grid-cols-12 gap-6">
          <div class="col-span-12 lg:col-span-12">
            <div class="overflow-hidden">
              <div class="w-100">
                <div class="grid grid-cols-12 gap-6">
                  <div class="col-span-12 lg:col-span-6">
                    <div class="bg-white shadow-xl sm:rounded-lg h-[26rem] message-detail">
                      <div class="grid grid-cols-12 gap-6">
                        <div class="col-span-12 lg:col-span-6 p-4 sm:p-6 lg:p-5">
                          <div class="mb-5">
                            <h2 class="font-semibold text-xl text-gray-800 leading-tight mb-2">Message Details</h2>
                          </div>
                          <div class="mb-5">
                            <div class="text-gray-600">Sent to</div>
                            <p v-if="messageDetails.group">
                              <Link :href="route('groups.view', messageDetails.group.tra_id)" class="text-blue-600 cursor-pointer">
                                {{ messageDetails.group.title }} <template v-if="messageDetails.group.deleted_at"> (Deleted List)</template>
                              </Link>
                            </p>
                            <p v-else-if="messageDetails.contacts_messaged == 1">
                              <Link :href="route('contacts.view', messageDetails.contact.id)" class="text-blue-600 cursor-pointer">
                                {{ messageDetails.sent_to }}
                              </Link>
                            </p>
                            <p v-else>
                              {{ messageDetails.contacts_messaged }} contacts
                            </p>

                          </div>
                          <div class="mb-5">
                            <div class="text-gray-600">Date sent</div>
                            <p><date-tooltip :data="messageDetails.date_sent" :showTooltip="false"/></p>
                          </div>
                          <div class="mb-5" v-if="messageDetails.time_sent">
                            <div class="text-gray-600">Time sent</div>
                            <p>{{ messageDetails.time_sent }}</p>
                          </div>
                          <div class="mb-5">
                            <div class="text-gray-600">Sent by</div>
                            <p>{{ messageDetails.sent_by }}</p>
                          </div>
                          <div class="mb-5">
                            <div class="text-gray-600">Credits used</div>
                            <p>{{ numberFormat(messageDetails.credits_used) }}</p>
                          </div>
                        </div>
                        <div class="col-span-12 lg:col-span-6 pt-4 sm:pt-6 lg:pt-5 pr-4 sm:pr-6 lg:pr-5">
                          <div class="message-preview-wrapper">
                            <div class="message-preview-wrapper-inner" ref="messagePreviewWrapperInner">
                              <div class="message-preview">
                                <img
                                  v-if="messageDetails.media"
                                  :src="messageDetails.media"
                                  class="block w-full rounded-lg mb-3"
                                  alt="preview of image attachment"
                                />
                                <div
                                  v-if="messagePreview"
                                  class="p-3 text-white bg-blue-500 rounded-lg"
                                  v-html="messagePreview"
                                ></div>
                                <div v-else-if="!messagePreview && !messageDetails.media" class="p-3 text-gray-700 bg-gray-100 rounded">
                                  Enter a message to show preview...
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="col-span-12 lg:col-span-6">
                    <div class="bg-white shadow-xl p-4 sm:rounded-lg sm:p-6 lg:p-5 h-[26rem] message-analytics">
                      <div class="mb-3">
                        <h2 class="font-semibold text-xl text-gray-800 leading-tight">Message Analytics</h2>
                      </div>
                      <div class="flex space-x-2">
                        <div class="p-4 bg-gray-200 text-center rounded-md w-40">
                          <h3 class="font-semibold text-xs text-gray-500 leading-tight">Contacts messaged</h3>
                          <p class="text-base">{{ numberFormat(messageDetails.contacts_messaged) }}</p>
                        </div>
                        <div class="p-4 bg-gray-200 text-center rounded-md w-40" v-if="messageDetails.has_links">
                          <h3 class="font-semibold text-xs text-gray-500 leading-tight">Link clicks</h3>
                          <p class="text-base">{{ numberFormat(messageDetails.link_visits) }}</p>
                        </div>
                        <div class="p-4 bg-gray-200 text-center rounded-md w-40">
                          <h3 class="font-semibold text-xs text-gray-500 leading-tight">Delivery Rate</h3>
                          <p class="text-base">{{ numberFormat(delivered) }} ({{ deliveryRate }})</p>
                        </div>
                      </div>
                       <Bar v-if="loaded" :data="data" :options="options" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="overflow-hidden mt-5">
              <div class="w-full">
                  <div class="bg-white shadow-xl p-4 sm:rounded-lg sm:p-6 lg:p-8">
                      <h2
                          class="font-semibold text-xl text-gray-800 leading-tight mb-5"
                      >
                      Contacts Messaged
                      </h2>
                      <contact-messaged :messageDeliverableId="messageDetails.message_deliverable_id" :hasLinks="messageDetails.has_links"></contact-messaged>
                  </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </app-layout>
</template>

<script>
import AppLayout from "../../Layouts/AppLayout";
import JetButton from "../../Jetstream/Button";
import MessageForm from "../../components/MessageForm";
import ContactMessaged from "../../components/ContactMessaged.vue";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
} from 'chart.js'
import { Bar } from 'vue-chartjs'
import ChartJsPluginDataLabels from 'chartjs-plugin-datalabels'

const numberFormatter = new Intl.NumberFormat();


ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartJsPluginDataLabels)

import axios from "axios";
export default {

  components: {
    AppLayout,
    JetButton,
    MessageForm,
    ContactMessaged,
    Bar
  },
  props: {
      message: {
          type: Object,
          default: null,
      },
  },
  data() {
    return {
        loaded: false,
      data: {
        labels: [
              ['Sent'],
              ['Sending'],
              ['Failed']
        ],
        datasets: [{
          data: [],
          barThickness: 50,
          fill: false,
          label: '',
          backgroundColor: [
            '#9AE6B4',
            '#9ACFE6',
            '#E69A9A'
          ],
          hoverBackgroundColor:[
            '#9AE6B4',
            '#9ACFE6',
            '#E69A9A'
          ]
        }],
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        layout: {
          padding: {
            right: 150
          }
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: false
          },
          datalabels: {
              anchor: 'start',
              align: 'right',
              offset: 10,
              color: function(context) {
                  var index = context.dataIndex;
                  var value = context.dataset.data[index];

                  let sum = context.dataset.data.reduce((a, b) => a + b, 0);
                  let percentage = Math.round((value / sum) * 100);

                  if(percentage <= 7) {
                      return value = 'black';
                  } else {
                      return value = 'white';
                  }
              },
              clamp: true,
              textAlign: 'center',
              formatter: (value) => numberFormatter.format(value),
              display: function (context) {
                return context.dataset.data[context.dataIndex] !== 0; // or >= 1 or ...
              },
              font: {
                weight: 'bold',
                size: 14,
              }
            }
        },
        scales: {
          x:{
            grid: {
              drawOnChartArea: false,
            },
            max: (data) => {
              return data.chart.data.datasets[0].data.reduce((a, b) => a + b, 0)
            },
            display: false
          },
          y:{
            ticks: {
              padding: 10,
              color: ['#A0AEC0'],
              	font: {
                	size: 12,
                },
            },
            gridLines: {
              display: false
            },
            grid: {
              drawOnChartArea: false,
              display: false
            },
          }
        },
        }
    }
  },
  computed: {
            delivered() {
                if (this.messageDetails.media) {
                    return this.totalMessages - this.messageDetails.message_failed;
                } else {
                    return this.messageDetails.message_delivered;
                }
            },
            deliveryRate() {
                let percent = this.messageDetails.message_delivered / this.messageDetails.contacts_messaged * 100;
                if (this.messageDetails.media) {
                    //in this case, percent should be total minus failed
                    percent = (this.totalMessages - this.messageDetails.message_failed) / this.totalMessages * 100;
                }

                return percent.toFixed(0) + '%';
            },
            totalMessages() {
                return this.messageDetails.message_sent + this.messageDetails.message_delivered + this.messageDetails.message_failed;
            },
            messagePreview() {
                const pattern = /{\s*([a-zA-Z]+)(\s+(default)\=[\'\"]([^\'\"\n]*)[\'\"])?\s*}/g;
                const fieldsMap = {
                    'firstName': 'first_name',
                    'lastName': 'last_name',
                };
                const matches = [...this.messageDetails.message.matchAll(new RegExp(pattern))];

                var newMessage = this.messageDetails.message.replace(/\n/g, '<br />');

                matches.forEach(match => {
                    const fieldKey = fieldsMap.hasOwnProperty(match[1]) ?
                        fieldsMap[match[1]] : null;
                    const defaultValue = match[4];

                    const fieldValue = (
                        this.contact &&
                        fieldKey &&
                        this.contact[fieldKey] &&
                        this.contact[fieldKey].length > 0
                     ) ? this.contact[fieldKey] : null;

                    const replaceWith = fieldValue ?
                        fieldValue :
                        defaultValue ? defaultValue : null;

                    const helpText = 'This is a variable. It will be replaced with the corresponding field for each contact. If the field is not available, it will look for a default value. If no default is found, the variable will be omitted.';

                    const variableHtml = replaceWith ?
                        `<span class="message-preview-variable" title="${helpText}">${replaceWith}</span>` :
                        '';

                    newMessage = newMessage.replace(match[0], variableHtml);
                });

                return newMessage;
            },
            messageDetails() {
              return this.message.data
            },
    },

  methods: {
    updateChartData() {
      if (this.message && this.message.data) {
        this.data.datasets[0].data = [
          this.message.data.message_sent,
          this.message.data.message_sending,
          this.message.data.message_failed
        ];
      }
      this.loaded = true;
    },
    numberFormat(value) {
      if (value === null || typeof value === 'undefined') {
        return '--';
      }
      return new Intl.NumberFormat().format(value);
    }
  },
  
  mounted() {
    this.updateChartData();
  },


};
</script>
<style scoped lang="scss">


@media(min-width: 1025px){
  .message-detail, .message-analytics{
    height: 425px;
  }
}

.message-preview-wrapper {
  @apply mx-auto px-9 pb-4 relative bg-no-repeat bg-top max-w-xs;
  padding-top: 105px;
  background-image: url("/images/iphone-wrapper-cropped.png");
  background-size: 100%;
  height: 100%;
}

.message-preview-wrapper-inner {
  @apply h-full relative pt-3 pb-5 rounded-lg overflow-auto;
  min-height: 204px;
  max-height: 400px;
}

.message-preview-wrapper:before {
  @apply absolute inset-x-8 top-16 h-6 z-10;
  content: "";
  background: linear-gradient(180deg, white, transparent);
}

.message-preview-wrapper:after{
  display: none !important;
}

.message-preview {
  @apply flex flex-col items-start justify-end;
  min-height: 180px;
}

@media (min-width: theme("screens.lg")) {
  .message-preview-wrapper-inner {
    min-height: 284px;
  }

  .message-preview {
    min-height: 240px;
  }
}

.message-preview-variable {
  @apply bg-blue-700 shadow leading-loose px-2 py-1 font-black;
  cursor: help;
}

span.label {
    text-transform: capitalize;
    cursor:help;
    &.delivered {
        @apply bg-green-300;
        @apply text-green-800;
    }
    &.received {
        @apply bg-blue-300;
        @apply text-blue-800;
    }
    &.pending {
        @apply bg-yellow-300;
        @apply text-yellow-800;
    }
    &.undelivered, &.error, &.failed, &.attempt_rejected {
        @apply bg-red-300;
        @apply text-red-800;
    }
}
</style>
